import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useAuth } from '@shared/hooks/useAuth';
import { container } from '@infrastructure/container';
import { useAuthStore } from '../../../application/store/AuthStore';
import { resolvePostAuthRedirect } from '../../../features/connections/shopify/pendingInstall';
import { Company } from '@domain/account/models/Company';
import { User } from '@domain/account/models/User';

interface CompanySelectLocationState {
  redirect?: string;
  user?: User;
  jwt?: string;
}

const CompanySelect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const locationState = (location.state ?? {}) as CompanySelectLocationState;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirect = locationState.redirect ?? resolvePostAuthRedirect();

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const result = await container.companyRepository.getCompanies();
        if (result.status === 'error' || !result.data?.length) {
          setError('No companies found for this account');
          return;
        }

        if (result.data.length === 1) {
          await selectCompany(result.data[0]);
          return;
        }

        setCompanies(result.data);
      } catch {
        setError('Failed to load companies');
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanies();
  }, []);

  const getUser = async (): Promise<User | null> => {
    if (locationState.user) {
      return locationState.user;
    }

    const userResponse = await container.authRepository.getCurrentUser()
    return userResponse.status === 'success' ? userResponse.data : null;
  };

  const resolveSession = async (): Promise<{ user: User; jwt: string } | null> => {
    const user = await getUser();
    const jwt = locationState.jwt ?? (await useAuthStore.getState().getJwt());

    if (!user || !jwt) {
      return null;
    }

    return { user, jwt };
  };

  const selectCompany = async (company: Company) => {
    setIsSelecting(true);
    setError(null);

    try {
      const session = await resolveSession();
      if (!session) {
        setError('Your session has expired. Please sign in again.');
        navigate('/sign-in');
        return;
      }

      await login(session.user, session.jwt, company);
      navigate(redirect, { replace: true });
    } catch {
      setError('Failed to select company');
    } finally {
      setIsSelecting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 0,
      }}
    >
      <DialogContainer>
        <DialogHeader>
          <img height={16} src="/assets/images/ic_just_stock.png" alt="logo" />
        </DialogHeader>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 280 }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              Select a company
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your account has access to multiple companies. Choose which one to open.
            </Typography>
          </Box>

          {isLoading && (
            <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          )}

          {!isLoading && error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {!isLoading && !error && (
            <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
              {companies.map((company) => (
                <ListItemButton
                  key={company.id}
                  onClick={() => selectCompany(company)}
                  disabled={isSelecting}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemText
                    primary={company.name}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          <Button
            variant="outlined"
            onClick={() => navigate('/sign-in')}
            disabled={isSelecting}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back to sign in
          </Button>
        </Box>
      </DialogContainer>
    </Box>
  );
};

export default CompanySelect;
